import { Dispatch, SetStateAction, useEffect } from "react";
import { loadCountryConfigFiles } from "../countryConfigLoader";
import { addDisbursementDate } from "pages/employer-of-record/payments/utils";
import { AdditionalPayContextProps } from "pages/employer-of-record/payments/contexts/additionalPayContext";
import { FieldConfig } from "../../SetupFormGenerator";
import {
  CountryConfigKeyType,
  ReimbursementPayloadType,
  SubdivisionConfigKeyType,
} from "../types";
import { useFormikContext } from "formik";
import { AdditionalPayFormData } from "../../../types";
import { DecoratedMessageWithLinkType } from "pages/employer-of-record/components/MessageWithLink/types";

const useCountryConfigFetcher = (
  additionalPayContext: AdditionalPayContextProps | null,
  setPaymentSettingsFields: Dispatch<SetStateAction<FieldConfig[]>>,
  setExpenseDetailsFields: Dispatch<SetStateAction<FieldConfig[]>>,
  setExpenseTypes: Dispatch<SetStateAction<string[]>>,
  setMessage: Dispatch<SetStateAction<DecoratedMessageWithLinkType | null>>
) => {
  const selectedCountry = additionalPayContext?.selectedCountry;
  const selectedSubdivision = additionalPayContext?.selectedSubdivision;

  const { values } =
    useFormikContext<AdditionalPayFormData<ReimbursementPayloadType>>();
  const isLocationReady = additionalPayContext?.isLocationReady ?? false;

  useEffect(() => {
    const fetchCountryConfigs = () => {
      if (!isLocationReady) return;

      const configs = loadCountryConfigFiles(
        selectedCountry as CountryConfigKeyType,
        selectedSubdivision as SubdivisionConfigKeyType
      );
      if (!configs) {
        setPaymentSettingsFields([]);
        setExpenseDetailsFields([]);
        setExpenseTypes([]);
        setMessage(null);
        return;
      }
      let paymentSettingsFormFields = configs.PAYMENT_SETTINGS_FIELDS;

      if (additionalPayContext?.disbursementDateOptions) {
        paymentSettingsFormFields = addDisbursementDate(
          paymentSettingsFormFields,
          Object.values(additionalPayContext?.disbursementDateOptions)
        );
      }

      configs.EXPENSE_DETAILS_FIELDS.map((val) => {
        if (val.name === "distance") {
          val.renderCheck = () => {
            return (
              configs.ReimbursementType.MILEAGE ===
              values.setUp.payload.paymentSettings?.expenseType
            );
          };
        }
      });

      setPaymentSettingsFields(paymentSettingsFormFields);
      setExpenseDetailsFields(configs.EXPENSE_DETAILS_FIELDS);
      setExpenseTypes(configs.EXPENSETYPES);

      const defaultMessage = configs.ReimbursementTypeMessage?.[""] || null;
      const message =
        configs.ReimbursementTypeMessage?.[
          values.setUp.payload.paymentSettings.expenseType || ""
        ] || defaultMessage;
      setMessage(message);
    };
    fetchCountryConfigs();
  }, [
    isLocationReady,
    selectedCountry,
    selectedSubdivision,
    additionalPayContext?.disbursementDateOptions,
    values.setUp.payload.paymentSettings.expenseType,
    setExpenseDetailsFields,
    setExpenseTypes,
    setPaymentSettingsFields,
    setMessage,
  ]);
};

export default useCountryConfigFetcher;
